/**
 * Модель канбана заявок менеджера/админа («Облака» #34).
 *
 * Бэк отдаёт state заявки сырым кодом домена (created/assigned/in_progress/
 * resolved/cancelled/escalated). Канбан по ТЗ — 4 колонки:
 *   Новые → В работе → Выполнено → Отменено.
 * Здесь маппинг state → колонка, разрешённые drag-переходы (→ какой API
 * вызвать), русские лейблы статусов/категорий и расчёт SLA/просрочки.
 *
 * i18n: НЕ показываем сырые англ-коды (state/категория) — всё в русский.
 */
import type { ManagerRequest } from '@hsc/types';

/** Ключи колонок канбана (порядок слева-направо). */
export type ColumnKey = 'new' | 'in_progress' | 'done' | 'cancelled';

export interface ColumnDef {
  key: ColumnKey;
  /** Заголовок колонки (RU). */
  title: string;
  /** Тон акцента колонки (CSS-класс rqk-col--{tone}). */
  tone: 'pending' | 'active' | 'done' | 'cancelled';
}

/** Колонки канбана по ТЗ — порядок фиксирован. */
export const COLUMNS: ReadonlyArray<ColumnDef> = [
  { key: 'new', title: 'Новые', tone: 'pending' },
  { key: 'in_progress', title: 'В работе', tone: 'active' },
  { key: 'done', title: 'Выполнено', tone: 'done' },
  { key: 'cancelled', title: 'Отменено', tone: 'cancelled' },
];

/**
 * Сырой state заявки → колонка канбана.
 *  created / assigned / escalated → «Новые» (ещё не взяты в работу)
 *  in_progress                    → «В работе»
 *  resolved                       → «Выполнено»
 *  cancelled                      → «Отменено»
 */
export function columnOf(state: string): ColumnKey {
  switch (state) {
    case 'in_progress':
      return 'in_progress';
    case 'resolved':
      return 'done';
    case 'cancelled':
      return 'cancelled';
    default:
      // created / assigned / escalated / new / open / прочее
      return 'new';
  }
}

/** Человекочитаемый статус заявки (RU) — без сырых англ-кодов. */
const STATE_LABELS: Readonly<Record<string, string>> = {
  created: 'Создана',
  new: 'Новая',
  open: 'Открыта',
  assigned: 'Назначена',
  in_progress: 'В работе',
  resolved: 'Выполнена',
  cancelled: 'Отменена',
  escalated: 'На контроле',
};

export function stateLabel(state: string): string {
  return STATE_LABELS[state] ?? state;
}

/** Приоритет заявки (RU). */
const PRIORITY_LABELS: Readonly<Record<string, string>> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочный',
};

export function priorityLabel(priority: string): string {
  return PRIORITY_LABELS[priority] ?? priority;
}

/**
 * Русский лейбл категории заявки. Категории — пер-тенантные слаги, мапим
 * известные; неизвестные показываем как есть (но человекочитаемо: подчёрки
 * → пробелы, первая заглавная).
 */
const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  room_service: 'Room service',
  'room-service': 'Room service',
  housekeeping: 'Уборка',
  maintenance: 'Поломка',
  repair: 'Поломка',
  food_beverage: 'F&B (бар/кухня)',
  'food-beverage': 'F&B (бар/кухня)',
  fnb: 'F&B (бар/кухня)',
  food: 'Кухня',
  bar: 'Бар',
  kitchen: 'Кухня',
  spa: 'СПА',
  concierge: 'Консьерж',
  transfer: 'Трансфер',
  excursion: 'Экскурсии',
  hookah: 'Кальян',
  other: 'Прочее',
};

export function categoryLabel(category: string): string {
  if (category === '') return 'Без категории';
  const known = CATEGORY_LABELS[category];
  if (known) return known;
  // F&B-подкатегории (kitchen_zavtraki, bar_kofe …) — не показываем сырой код,
  // сворачиваем по префиксу в «Кухня»/«Бар».
  const lc = category.toLowerCase();
  if (lc.startsWith('kitchen')) return 'Кухня';
  if (lc.startsWith('bar') || lc.startsWith('minibar')) return 'Бар';
  const human = category.replace(/[_-]+/g, ' ').trim();
  return human.charAt(0).toUpperCase() + human.slice(1);
}

/* ===================== Идентификатор / исполнитель ===================== */

/**
 * Главный человекочитаемый идентификатор заявки — простой порядковый
 * номер «#N» (правка Артёма: «должно быть простое порядковое», а не номер
 * номера/хвост uuid). Бэк отдаёт seq (миграция 0060). Для очень старых
 * строк до бэкфилла (seq=0/нет) деградируем на короткий хвост uuid, чтобы
 * карточка никогда не показывала пустоту.
 */
export function requestNumber(req: ManagerRequest): string {
  if (typeof req.seq === 'number' && req.seq > 0) return `#${req.seq}`;
  return `#${req.id.slice(0, 8)}`;
}

/**
 * Подпись комнаты гостя — «№<room_number>», как в «Проживаниях» (правка
 * Артёма: показывать номер комнаты, а не uuid проживания). Бэк резолвит
 * room_number по проживанию (stay → rooms.number). '' — если заявка не
 * привязана к проживанию или номер не резолвился (карточка просто без неё).
 */
export function stayLabel(req: ManagerRequest): string {
  const room = req.room_number?.trim();
  if (room) return `№${room}`;
  return '';
}

/**
 * Подпись гостя заявки — фамилия/имя, как в «Проживаниях». Бэк резолвит
 * guest_name по проживанию (stay → guests). '' — если гость не резолвится
 * (заявка без проживания); карточка тогда не показывает строку гостя.
 */
export function guestLabel(req: ManagerRequest): string {
  return req.guest_name?.trim() ?? '';
}

/**
 * Подпись исполнителя для карточки/деталей. Имя приходит с бэка
 * (assignee_name). Если не назначен — «Не назначен».
 */
export function assigneeLabel(req: ManagerRequest): string {
  const name = req.assignee_name?.trim();
  if (name) return name;
  // Никогда не показываем сырой uuid — если имя не резолвилось, заявка
  // для UI считается без исполнителя (правка Артёма).
  return 'Не назначен';
}

/** Назначена ли заявка на сотрудника. */
export function isAssigned(req: ManagerRequest): boolean {
  return Boolean(req.assigned_staff_id);
}

/**
 * Инициалы исполнителя для аватар-чипа на карточке (1-2 буквы). Для
 * не назначенной — «—».
 */
export function assigneeInitials(req: ManagerRequest): string {
  const name = req.assignee_name?.trim();
  // Без резолвнутого имени — «—» (не uuid, не «#»): UI трактует как
  // не назначена, даже если assigned_staff_id есть, но имя не нашли.
  if (!name) return '—';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * Группировка фильтра по типу (ТЗ #34): room service / поломка / F&B.
 * Сопоставляет произвольный слаг категории в одну из трёх групп (или null).
 */
export type TypeFilter = 'all' | 'room_service' | 'maintenance' | 'fnb';

export const TYPE_FILTERS: ReadonlyArray<{ value: TypeFilter; label: string }> = [
  { value: 'all', label: 'Все типы' },
  { value: 'room_service', label: 'Room service' },
  { value: 'maintenance', label: 'Поломки' },
  { value: 'fnb', label: 'F&B (бар/кухня)' },
];

export function typeGroupOf(category: string): TypeFilter | null {
  const c = category.toLowerCase();
  if (c.includes('room') && c.includes('serv')) return 'room_service';
  if (c.includes('maint') || c.includes('repair') || c.includes('полом')) return 'maintenance';
  if (
    c.includes('food') ||
    c.includes('beverage') ||
    c.includes('fnb') ||
    c === 'bar' ||
    c === 'kitchen' ||
    c.includes('hookah')
  ) {
    return 'fnb';
  }
  return null;
}

export function matchesType(req: ManagerRequest, filter: TypeFilter): boolean {
  if (filter === 'all') return true;
  return typeGroupOf(req.category) === filter;
}

/* ===================== SLA / просрочка ===================== */

export interface SLAInfo {
  /** SLA задан и срок уже прошёл (заявка ещё не завершена). */
  breached: boolean;
  /** SLA задан, срок ещё не наступил. */
  hasDeadline: boolean;
  /** Минут до дедлайна (отрицательно если просрочено). null — нет SLA. */
  minutesLeft: number | null;
  /** Готовая RU-подпись для бейджа SLA. */
  label: string;
}

/** Завершённые состояния — SLA уже не «горит». */
function isTerminalState(state: string): boolean {
  return state === 'resolved' || state === 'cancelled';
}

/**
 * Расчёт SLA-индикатора карточки. `now` инъектируется для тестируемости.
 */
export function slaInfo(req: ManagerRequest, now: number = Date.now()): SLAInfo {
  if (!req.sla_due_at) {
    return { breached: false, hasDeadline: false, minutesLeft: null, label: 'без SLA' };
  }
  const due = new Date(req.sla_due_at).getTime();
  if (Number.isNaN(due)) {
    return { breached: false, hasDeadline: false, minutesLeft: null, label: 'без SLA' };
  }
  const terminal = isTerminalState(req.state) || Boolean(req.resolved_at);
  const minutesLeft = Math.round((due - now) / 60_000);
  if (terminal) {
    return { breached: false, hasDeadline: true, minutesLeft, label: 'SLA закрыт' };
  }
  if (minutesLeft < 0) {
    return {
      breached: true,
      hasDeadline: true,
      minutesLeft,
      label: `просрочено ${formatMinutes(-minutesLeft)}`,
    };
  }
  return {
    breached: false,
    hasDeadline: true,
    minutesLeft,
    label: `SLA через ${formatMinutes(minutesLeft)}`,
  };
}

/** «125» минут → «2 ч 5 мин»; до часа — «N мин». */
export function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

/* ===================== Переходы канбана (force-status) ===================== */

/**
 * Канон-state бэка, в который переводим заявку при дропе в колонку.
 * Менеджер форсит статус через PATCH /v1/admin/requests/{id}/status — бэк
 * НЕ проверяет строгий FSM (нет 412), поэтому ЛЮБОЙ переход допустим,
 * включая «назад» (Выполнено → В работе) и «напрямую» (Новые → Выполнено).
 */
export type TargetState = 'created' | 'in_progress' | 'resolved' | 'cancelled';

/** Целевой статус заявки для каждой колонки канбана. */
export const COLUMN_TARGET_STATE: Readonly<Record<ColumnKey, TargetState>> = {
  new: 'created',
  in_progress: 'in_progress',
  done: 'resolved',
  cancelled: 'cancelled',
};

/**
 * Целевой статус при перетаскивании заявки из колонки `from` в `to`.
 * null — только если дроп в ту же колонку (нет смысла). Любой ДРУГОЙ
 * переход разрешён (force-status: менеджер может в любой статус всегда).
 */
export function transitionFor(from: ColumnKey, to: ColumnKey): TargetState | null {
  if (from === to) return null;
  return COLUMN_TARGET_STATE[to];
}
